// Whether to show the engineer PWA "add to home screen" nudge, and its dismissal (Session ADU).
//
// The engineer PWA otherwise persists only through IndexedDB (the offline job loop). This is a
// deliberately tiny, synchronous boolean the nudge reads on every render, so localStorage is the
// right tool, not IndexedDB. All access is window-guarded and try/catch-swallowed, matching the
// house shape in src/lib/quoteAutosave.ts and src/lib/navExpanded.ts (SSR/quota/private-mode safe).
//
// Persistence is localStorage, NOT sessionStorage: a "you should install this" nudge, once
// dismissed, must STAY dismissed. sessionStorage would re-show it on every new browser session,
// which on a phone is close to every day -- the exact naggy behaviour a nudge must avoid.
const DISMISS_KEY = "ele.engineerInstallNudge.dismissed";

// True when the app is already running as an INSTALLED PWA (standalone display mode on
// Android/Chrome/desktop, or iOS Safari's legacy navigator.standalone). In that case the engineer
// has already installed it and the nudge would be pure noise, so it is suppressed. Guarded because
// jsdom has neither matchMedia nor navigator.standalone.
export function isInstalledPwa(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  } catch {
    /* older browsers / jsdom: fall through to the iOS check */
  }
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  return iosStandalone === true;
}

export function installNudgeDismissed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

export function dismissInstallNudge(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DISMISS_KEY, "1");
  } catch {
    /* private mode / quota: best-effort, the nudge simply reappears next load */
  }
}

// Whether the nudge should render now: not already installed, and not previously dismissed.
export function shouldShowInstallNudge(): boolean {
  return !isInstalledPwa() && !installNudgeDismissed();
}
